<script>
import LogRocket from 'logrocket'
import MottoSidebar from '@components/sidebar.vue'
import MottoFooter from '@components/motto-footer.vue'
import MottoIcon from '@components/motto-icon.vue'
import ProfilePicture from '@components/profile-picture.vue'
import SearchResourceInput from '@components/search/search-resource-input.vue'
import ScrollToTopButton from '@components/scroll-to-top-button.vue'
import NotificationInBell from '@components/notifications/notification-in-bell.vue'
import MileCountdown2026 from '@components/mile-countdown-2026.vue'
import DebugMode from '@components/debug-mode.vue'
import SurveyRider from '@components/survey/survey-rider.vue'
import { authComputed, debugOptionsComputed, resourcesComputed } from '@state/helpers'
import { isMobileMixin } from '@src/mixins/isMobileMixin'
import { getAllNotifications, hasUnreadNotifications, readAllMyNotifications } from '@src/api/notifications'
import { getPlatform } from '@src/api/tracking'
import ResizeObserver from 'resize-observer-polyfill'
import { getMySurveys } from '@src/api/survey'
import _ from 'lodash'
import moment from 'moment/moment'
import ModalCourseReminder from '@components/education/modal-course-reminder.vue'
import ProspectPromptHandler from '@components/prospect-prompt-handler.vue'

export default {
  components: {
    ProspectPromptHandler,
    ModalCourseReminder,
    MottoSidebar,
    MottoFooter,
    MottoIcon,
    ProfilePicture,
    SearchResourceInput,
    NotificationInBell,
    ScrollToTopButton,
    SurveyRider,
    MileCountdown2026,
    DebugMode,
  },
  mixins: [isMobileMixin],
  data() {
    return {
      tabFocus: true,
      sidebarExpanded: false,
      notifications: [],
      notificationsRead: false,
      leftSidebarOffset: 0,
      currentSurvey: {},
      debugMode: process.env.APP_ENV !== 'prod',
      shouldUpdateTitle: true,
      originalTitle: document.title.replace(/ *\([^)]*\) */g, '') || '',
    }
  },
  computed: {
    ...authComputed,
    ...resourcesComputed,
    ...debugOptionsComputed,

    pageHasSurvey() {
      return !_.isEmpty(this.currentSurvey)
    },

    mileCountdownPosition() {
      return this.$screen.width < 656 ? 'full-width' : 'header'
    },

    getUserName() {
      if (!this.currentUser) {
        return ''
      }

      if (!this.currentUser.name) {
        return ''
      }

      if (!this.isMobile) {
        return this.currentUser.name
      }

      const fullName = this.currentUser.name.split(' ')
      const firstName = fullName[0]
      const lastName = fullName[fullName.length - 1]
      const firstLetter = firstName.charAt(0)
      return `${firstLetter}. ${lastName}`
    },

    unreadNotifications() {
      return this.notifications.filter((notification) => notification.status !== 'READ')
    },

    unreadNotificationAmount() {
      if (this.unreadNotifications.length > 9) {
        return '9+'
      }

      return this.unreadNotifications.length
    },

    courseReminderNotifications() {
      return this.notifications.filter((notification) => {
        return notification.type === 'DOCEBO_COURSE_REMINDER' && notification.status !== 'READ'
      })
    },

    getLeftSpacingMargin() {
      if (this.isMobile) {
        return 0
      }

      const sidebarWidth = 273

      return sidebarWidth - this.leftSidebarOffset
    },
  },
  watch: {
    adminAuthError: function () {
      this.checkAdminAuthErrors()
    },
    originalTitle() {
      this.updateTitleWithNotifications()
    },
    unreadNotificationAmount() {
      this.updateTitleWithNotifications()
    },
  },
  beforeCreate() {
    document.body.className = 'admin'
  },
  created() {
    document.addEventListener('click', this.handlePageClick)
    this.listenToTitleChanges()
    this.loadNotifications()
    this.checkAdminAuthErrors()
    this.identifyLogRocketUser()
    this.checkSurvey()
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handlePageClick)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    this.resizeObserver.unobserve(this.$refs.adminMainContentWrapper)
  },

  mounted() {
    if (this.shouldStartNotificationLoop) {
      this.$store.commit('auth/SET_SHOULD_START_NOTIFICATION_LOOP', false)
      this.checkUnreadNotificationsLoop()
    }
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    this.resizeObserver = new ResizeObserver(this.onResize)
    this.resizeObserver.observe(this.$refs.adminMainContentWrapper)
  },
  methods: {
    expandSidebar() {
      this.sidebarExpanded = true
    },
    collapseSidebar() {
      this.sidebarExpanded = false
    },

    onResize() {
      const desiredAdminWrapperWidth = 1000
      const sidebarWidth = 273
      const currentWrapperRightMargin = 220
      const screenSizeToStartResizing = desiredAdminWrapperWidth + sidebarWidth + currentWrapperRightMargin
      const minimumMargin = 110
      if (window.innerWidth < screenSizeToStartResizing) {
        this.leftSidebarOffset = Math.min(minimumMargin, screenSizeToStartResizing - window.innerWidth)
      } else if (window.innerWidth > screenSizeToStartResizing) {
        this.leftSidebarOffset = 0
      }
    },

    handlePageClick(clickEvent) {
      if (this.shouldClickCollapseSidebar(clickEvent)) {
        this.collapseSidebar()
      }
    },

    updateTitleWithNotifications() {
      if (this.originalTitle) {
        this.shouldUpdateTitle = false
        if (this.unreadNotificationAmount) {
          document.title = `(${this.unreadNotificationAmount}) ${this.originalTitle}`
        } else {
          document.title = `${this.originalTitle}`
        }
      }
    },

    listenToTitleChanges() {
      const target = document.querySelector('title')
      const observer = new MutationObserver((mutations) => {
        mutations.forEach(() => {
          if (this.shouldUpdateTitle) {
            this.originalTitle = `${document.title}`
          }
          this.shouldUpdateTitle = true
        })
      })
      const config = {
        childList: true,
      }
      observer.observe(target, config)
    },

    shouldClickCollapseSidebar(clickEvent) {
      const elemTree = clickEvent.composedPath()

      const isClickOnHamburger = elemTree.some((node) => {
        try {
          return node?.getAttributeNames().includes('mobile-sidebar-hamburger')
        } catch (err) {
          return false
        }
      })

      if (isClickOnHamburger) {
        return false
      }

      const isClickOnSidebar = elemTree.some((node) => {
        try {
          return node?.getAttributeNames().includes('data-sidebar')
        } catch (err) {
          return false
        }
      })

      return this.sidebarExpanded && this.isMobile && !isClickOnSidebar
    },

    async readAllNotifications() {
      this.notificationsRead = true
      await readAllMyNotifications()
      await this.loadNotifications()
    },
    async loadNotifications() {
      try {
        const {
          data: { data: notifications },
        } = await getAllNotifications({ limit: 10 })
        this.notifications = notifications
      } catch (e) {}
    },

    async checkUnreadNotifications() {
      try {
        const {
          data: { hasUnread },
        } = await hasUnreadNotifications()

        if (hasUnread) {
          this.notificationsRead = false
          await this.loadNotifications()
        }
      } catch (e) {}
    },

    checkUnreadNotificationsLoop() {
      if (this._isDestroyed) {
        return
      }

      setTimeout(async () => {
        if (this.tabFocus && !this.unreadNotifications.length) {
          await this.checkUnreadNotifications()
        }
        this.checkUnreadNotificationsLoop()
      }, 30000)
    },

    handleVisibilityChange() {
      this.tabFocus = !document.hidden

      if (this.tabFocus) {
        this.$store
          .dispatch('auth/validate')
          .then(() => {})
          .catch((e) => {
            if (e?.message === 'NO_TOKEN_FOUND') {
              this.$router.push({
                name: 'login',
              })
            }
          })
      }
    },

    checkAdminAuthErrors() {
      if (!this.adminAuthError) {
        return
      }

      this.$store.dispatch('auth/logOut')
      if (window.location.pathname.length > 1) {
        this.$router.push({
          name: 'login',
          query: { returnUrl: window.location.pathname + window.location.search },
        })
        return
      }
      this.$router.push({
        name: 'login',
      })
    },

    async checkSurvey() {
      try {
        const {
          data: { data: surveys },
        } = await getMySurveys()

        const currentSurvey = _.first(
          surveys.filter((x) => {
            const pathName = window.location.origin + window.location.pathname
            return x?.promptUrl === pathName || x?.promptUrl === pathName.substring(0, pathName.length - 1)
          })
        )
        this.currentSurvey = currentSurvey ?? {}
        this.$store.commit('survey/SET_CURRENT_PAGE_SURVEY', this.currentSurvey)
      } catch (err) {
        this.currentSurvey = {}
      }
    },

    identifyLogRocketUser() {
      if (!this.currentUser || !this.$store.getters['user/shouldInitLogRocket']) {
        return
      }

      const isUsingApp = ['fullscreen', 'standalone', 'minimal-ui'].some(
        (displayMode) =>
          window.matchMedia('(display-mode: ' + displayMode + ')').matches || window.navigator.standalone === true
      )

      let {
        name: userName,
        primaryEmail: userEmail,
        loPhase,
        loPhaseChangedAt,
        activationDate,
        account,
        groups,
      } = this.currentUser

      loPhaseChangedAt = loPhaseChangedAt || moment().format()
      activationDate = activationDate || moment().format()
      const daysLoPhase = moment().diff(moment(loPhaseChangedAt), 'days')
      const daysActivated = moment().diff(moment(activationDate), 'days')
      const accountLabel = account.label

      const objLogRocketUser = {
        name: userName,
        email: userEmail,
        platform: isUsingApp ? 'App' : 'Website',
        'Lo Phase Two': loPhase === 2,
        '(60) New LO Phase 2': daysLoPhase <= 60 && loPhase === 2,
        '(30) New User': daysActivated <= 30,
        '(60) New User': daysActivated <= 60,
        [accountLabel]: true,
      }
      if (account.value === 'motto_membership') {
        const groupsLabel = groups.map((i) => i.label)
        for (const groupLabel of groupsLabel) {
          objLogRocketUser[groupLabel] = true
        }
      }

      LogRocket.identify(userEmail, objLogRocketUser)
    },

    trackLogRocket(eventName) {
      if (!this.currentUser || !this.$store.getters['user/shouldInitLogRocket']) {
        return
      }
      LogRocket.track(eventName)
    },

    openHelp(e) {
      e.preventDefault()
      const tradename = this.currentUser?.primaryOffice?.tradeName
      const userName = this.currentUser.name || ''
      const now = new Date().toLocaleString()

      const subject = encodeURIComponent(`${tradename} - ${userName} - ${now}`)
      const recipient = 'help@mottomortgage.com'

      window.location.href = `mailto:${recipient}?subject=${subject}`
    },

    installPwaApp() {
      this.trackLogRocket('ClickDownloadApp')
      if (this.$store.getters['pwa/beforePwaInstallPrompt']) {
        this.$store.getters['pwa/beforePwaInstallPrompt'].prompt()
      }
    },

    getInstructionsLinks() {
      return process.env.APP_ENV === 'prod'
        ? 'https://www.mottocenter.com/resource/1274'
        : 'https://qa.mottocenter.com/resource/943'
    },

    requestPermissionPrompt() {
      this.trackLogRocket('ClickEnableNotifications')
      let promptTriggered = false
      const message =
        'You should have received a prompt to allow notifications from MottoCenter. If you did not receive a prompt, you have most likely blocked notifications.  Click here for instructions to turn notifications on.'

      Notification.requestPermission()
        .then((response) => {
          if (!['WEB', 'APP - WEB'].includes(getPlatform())) {
            return
          }
          if (response === 'denied') {
            this.$bvToast.toast(message, {
              toaster: 'b-toaster-bottom-center',
              toastClass: 'toast-warning',
              noAutoHide: true,
              href: this.getInstructionsLinks(),
            })
          }
        })
        .catch(() => {})
        .finally(() => {
          promptTriggered = true
        })
      setTimeout(
        function () {
          if (!promptTriggered) {
            this.$bvToast.toast(message, {
              toaster: 'b-toaster-bottom-center',
              toastClass: 'toast-warning',
              noAutoHide: true,
              href: this.getInstructionsLinks(),
            })
          }
        }.bind(this, promptTriggered, message),
        5000
      )
    },

    showEnableNotification() {
      if (typeof Notification === 'undefined') {
        return false
      }
      return Notification.permission !== 'granted'
    },
  },
}
</script>

<template>
  <div :class="$style.adminLayoutContainer">
    <MottoSidebar :sidebar-expanded="sidebarExpanded" @collapse="collapseSidebar" />
    <div :style="{ marginLeft: `${getLeftSpacingMargin}px`, marginTop: isMobile ? '120px' : '104px' }">
      <div :class="[$style.adminHeader]" :style="{ width: `calc(100% - ${getLeftSpacingMargin}px)` }">
        <div class="d-flex" style="min-width: 133px">
          <MottoIcon
            :class="$style.mobileHamburger"
            name="Hamburger"
            icon-color="primary"
            mobile-sidebar-hamburger
            @click.native="expandSidebar" />
          <RouterLink :to="{ name: 'home' }">
            <img :class="$style.headerLogo" src="@assets/images/mottocenter-new-logo-blue.png" alt="Logo" />
          </RouterLink>
        </div>
        <div :class="$style.searchContainer">
          <SearchResourceInput :class="$style.searchBar" />
        </div>
        <div :class="$style.profileContainer">
          <div v-if="mileCountdownPosition === 'header'" :class="$style.mileCountdownContainer">
            <MileCountdown2026 :height="104" />
          </div>
          <b-dropdown
            size="lg"
            variant="link"
            no-caret
            right
            toggle-class="text-decoration-none"
            :class="$style.notificationDropdown"
            @show="readAllNotifications">
            <template v-slot:button-content>
              <div :class="$style.notificationIconContainer">
                <MottoIcon
                  :class="[
                    $style.notificationIcon,
                    unreadNotifications.length && !notificationsRead ? $style.notificationIconAnimation : '',
                  ]"
                  name="Notification"
                  icon-color="slate" />
                <div v-show="unreadNotifications.length && !notificationsRead" :class="$style.notificationIconBadge">
                  {{ unreadNotificationAmount }}
                </div>
              </div>
            </template>
            <div :class="$style.notificationsContainer">
              <div>
                <h3>Notifications</h3>
              </div>
              <div class="d-flex flex-column">
                <NotificationInBell
                  v-for="notification in _.take(notifications, 10)"
                  :key="notification.id"
                  :notification="notification"></NotificationInBell>
                <p v-if="!notifications.length" class="text-center">You have no new notifications</p>
              </div>
            </div>
            <p
              class="button-text text-center cursor-pointer mb-2 color-dark-gray"
              @click="() => $router.push({ name: 'notifications' })">
              See all
            </p>
          </b-dropdown>
          <b-dropdown
            size="lg"
            variant="link"
            toggle-class="text-decoration-none"
            no-caret
            right
            :class="$style.profileDropdown"
            data-profile-dropdown>
            <template v-slot:button-content>
              <ProfilePicture
                :class="$style.profilePicture"
                hover-effect
                :src="currentUser.photoUrl"
                :active="$route.meta?.settingsMenu === true" />
              <p :class="$style.profileName">{{ getUserName }}</p>
            </template>
            <b-dropdown-item v-if="hasPersonnelFeature && !canManagePersonnel" href="/my-info">
              <div :class="$style.profileDropdownIconContainer">
                <BaseIcon :class="$style.profileDropdownIcon" name="user" />
              </div>
              Profile
            </b-dropdown-item>
            <b-dropdown-item v-if="hasPersonnelFeature && canManagePersonnel" href="/my-info">
              <div :class="$style.profileDropdownIconContainer">
                <BaseIcon :class="$style.profileDropdownIcon" name="users" />
              </div>
              Profile / Office Management
            </b-dropdown-item>
            <b-dropdown-item href="/faq">
              <div :class="$style.profileDropdownIconContainer">
                <MottoIcon
                  style="margin-left: -4px"
                  :class="$style.profileDropdownIcon"
                  name="Help"
                  icon-color="primary" />
              </div>
              FAQ
            </b-dropdown-item>
            <b-dropdown-item href="/mission-against-hunger">
              <div :class="$style.profileDropdownIconContainer">
                <BaseIcon :class="$style.profileDropdownIcon" name="utensils" />
              </div>
              Mission Against Hunger
            </b-dropdown-item>
            <b-dropdown-item href="/security">
              <div :class="$style.profileDropdownIconContainer">
                <BaseIcon
                  :class="$style.profileDropdownIcon"
                  :name="hasUserProfileFeature ? 'shield-halved' : 'gear'" />
              </div>
              Security Settings
            </b-dropdown-item>
            <b-dropdown-item v-if="hasUserProfileFeature && !hasPersonnelFeature" href="/profile">
              <div :class="$style.profileDropdownIconContainer">
                <BaseIcon :class="$style.profileDropdownIcon" name="user" />
              </div>
              Profile
            </b-dropdown-item>
            <b-dropdown-item v-if="$store.getters['pwa/beforePwaInstallPrompt']" @click="installPwaApp">
              <div :class="$style.profileDropdownIconContainer">
                <MottoIcon :class="$style.profileDropdownIcon" name="Download" icon-color="primary" />
              </div>
              <span class="no-wrap">Download App</span>
            </b-dropdown-item>
            <b-dropdown-item v-if="showEnableNotification()" @click="requestPermissionPrompt">
              <div :class="$style.profileDropdownIconContainer">
                <MottoIcon :class="$style.notificationDropdownIcon" name="Notification" icon-color="primary" />
              </div>
              <span class="no-wrap">Enable Notifications</span>
            </b-dropdown-item>
            <b-dropdown-item href="/logout">
              <div :class="$style.profileDropdownIconContainer">
                <BaseIcon :class="$style.profileDropdownIcon" name="right-from-bracket" />
              </div>
              Logout
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
      <div v-if="isMobile && mileCountdownPosition === 'full-width'" :class="$style.mileCountdownContainerMobile">
        <MileCountdown2026 :height="$screen.width / 4" />
      </div>
      <div v-if="isMobile" class="d-block d-lg-none" :class="$style.searchBarMobileContainer">
        <SearchResourceInput />
      </div>
      <div :class="$style.adminMainContentContainer">
        <div :class="$style.sidebarRightSpacing"></div>
        <div ref="adminMainContentWrapper" :class="$style.adminMainContentWrapper">
          <slot />
        </div>
      </div>
      <div :class="$style.legalInfoFooter">
        <div :class="$style.sidebarRightSpacing"></div>
        <div>
          <p class="tag-text color-gray">©{{ new Date().getFullYear() }} MOTTO FRANCHISING, LLC. ALL RIGHTS RESERVED</p>
          <p class="tag-text color-gray">
            <RouterLink :to="{ name: 'userAgreement' }">User Agreement</RouterLink>
            |
            <a href="https://www.mottomortgage.com/privacy-policy" target="_blank">Privacy Policy</a>
            |
            <template v-if="hasSmsPromptsFeature">
              <RouterLink :to="{ name: 'mobileTerms' }">Mobile Terms of Use</RouterLink>
              |
            </template>
            <a href="/" @click="openHelp">Help</a>
          </p>
        </div>
      </div>
    </div>
    <MottoFooter />
    <ScrollToTopButton v-if="isMobile" />
    <SurveyRider v-if="pageHasSurvey" :survey-question="currentSurvey" />
    <DebugMode v-if="debugMode && debugOptions" :debug-options.sync="debugOptions[$route.name]" />
    <ModalCourseReminder :courses-notifications="courseReminderNotifications" />
    <ProspectPromptHandler />
  </div>
</template>

<style lang="scss" module>
@import '@design';

$sidebar-width: 273px;
$sidebar-right-spacing: 133px;
$min-sidebar-right-spacing: 65px;
$admin-header-desktop-height: 104px;
$admin-header-mobile-height: 90px;

.adminLayoutContainer {
  @media (max-width: $lg-breakpoint) {
    display: flex;
    flex-direction: column;
    margin-bottom: 90px;
  }

  display: flex;
  flex-direction: column;
}

.adminMainContentWrapper {
  position: relative;
}

.profilePicture {
  margin-bottom: 4px;
}

.legalInfoFooter {
  @media (max-width: $lg-breakpoint) {
    width: auto;
  }

  display: flex;
  width: 100%;
  margin: 0 0 100px 0;
  text-align: center;

  > div:last-child {
    @media (max-width: $lg-breakpoint) {
      max-width: 100%;
      padding: 0 10px;
    }

    flex: 1 0 100%;
    max-width: calc(100% - 200px);

    a {
      color: $gray;
      text-decoration: underline;

      &:hover {
        color: $secondary-light-blue;
      }
    }
  }
}

.adminHeader {
  @media (max-width: $lg-breakpoint) {
    display: flex;
    width: 100%;
    height: $admin-header-mobile-height;
    margin-top: -120px;
    border-bottom: none;
  }

  position: fixed;
  z-index: 99;
  display: flex;
  height: $admin-header-desktop-height;
  margin-top: -$admin-header-desktop-height;
  background: $white;
  border-bottom: 1px solid $lightest-gray;
  box-shadow: 0 0 6px rgba(196, 196, 196, 0.3);

  .searchContainer {
    @media (max-width: $lg-breakpoint) {
      display: none;
    }

    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
  }

  .searchBar {
    width: 100%;
    min-width: 250px;
    max-width: 519px;
  }

  .profileContainer {
    @media (max-width: $lg-breakpoint) {
      align-items: start;
      padding-top: 0;
      margin-top: 24px;
      margin-right: 24px;
    }

    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-top: 10px;
    margin-right: 45px;
    margin-left: auto;

    .profileDropdown {
      @media (max-width: $lg-breakpoint) {
        margin-left: 0;
      }

      margin-left: 31.68px;

      .profileName {
        @media (max-width: $lg-breakpoint) {
          position: absolute;
          right: 0;
          bottom: -21px;
        }
      }

      button {
        padding: 0;
        text-align: -moz-center;
        text-align: -webkit-center;
        border: none;

        &:focus {
          box-shadow: unset;
        }

        p {
          @media (max-width: $lg-breakpoint) {
            font-size: 12px;
          }

          font-weight: bold;
          color: $darkest-gray;
        }
      }

      ul {
        min-width: 128px;
        max-width: 320px;
        padding: 6px 0;
        border: 1px solid #eef1f4;
        border-radius: 8px;
        box-shadow: 1px 2px 10px rgba(196, 196, 196, 0.4);

        li a {
          display: flex;
          align-items: center;
          padding: 12px 20px 12px 20px;
          font-size: 12px;
          font-weight: 700;
          color: $primary-blue;
          white-space: nowrap;

          &:active {
            background-color: #e9ecef;
          }
        }
      }

      .profileDropdownIconContainer {
        width: 16px;
        height: 16px;
        margin-right: 10px;
      }

      .profileDropdownIcon {
        width: 16px;
        font-size: 16px;

        svg {
          display: block;
          height: 16px;
        }
      }

      .notificationDropdownIcon {
        width: 16px;
        font-size: 16px;

        svg {
          display: block;
          width: 16px;
          height: 16px;
        }
      }
    }

    .notificationDropdown {
      button {
        @media (max-width: $lg-breakpoint) {
          margin-top: 0;
        }

        height: 32px;
        padding: 0;
        margin-top: -26px;

        &:focus {
          box-shadow: unset;
        }
      }

      ul {
        width: 330px;
        padding: 6px 0;
        border: 1px solid #eef1f4;
        border-radius: 8px;
        box-shadow: 1px 2px 10px rgba(196, 196, 196, 0.4);
      }
    }

    .notificationIconContainer {
      @media (max-width: $lg-breakpoint) {
        margin-right: 20px;
        transform: scale(0.9);
      }

      position: relative;
      min-width: 48px;
      padding: 8px;
      margin-top: -8px;
      margin-right: -3px;
      border-radius: 50%;

      &:hover {
        background: $lightest-gray;
      }
    }

    .notificationIcon {
      cursor: pointer;
    }

    .notificationIconAnimation {
      svg {
        animation-name: shake;
        animation-duration: 0.5s;
        animation-timing-function: ease-in-out;
        animation-delay: 0.5s;
        animation-iteration-count: 3;
      }
    }

    @keyframes shake {
      0%,
      100% {
        transform: rotate(0deg);
      }
      20%,
      60% {
        transform: rotate(12deg);
      }
      40%,
      80% {
        transform: rotate(-12deg);
      }
    }

    .notificationIconBadge {
      position: absolute;
      top: 2px;
      right: 2px;
      padding-block: 1px;
      padding-inline: 6px;
      font-size: 12px;
      font-weight: 700;
      line-height: 20px;
      color: $white;
      text-align: center;
      cursor: pointer;
      background: $secondary-red;
      border-radius: 8px;
    }

    .notificationsContainer {
      padding: 16px;

      > div:last-child {
        @media (max-width: $lg-breakpoint) {
          max-height: 220px;
        }

        max-height: 380px;
        overflow-y: scroll;
      }

      > p:last-child {
        text-decoration: underline;
      }
    }
  }
}

.searchBarMobileContainer {
  padding-right: 24px;
  padding-left: 24px;
  margin-bottom: 32px;
}

.mobileHamburger {
  @media (max-width: $lg-breakpoint) {
    display: block;
    margin-top: 29px;
    margin-left: 22px;
  }

  display: none;
}

.headerLogo {
  @media (max-width: $lg-breakpoint) {
    display: block;
    width: 128px;
    height: 24px;
    margin-top: 29px;
    margin-left: 30px;
  }

  display: none;
}

.adminMainContentContainer {
  @media (max-width: $lg-breakpoint) {
    max-width: unset;
    padding-top: 0;
    padding-right: 24px;
    padding-left: 24px;
  }

  display: flex;
  max-width: 100%;
  padding-top: 40px;
  padding-bottom: 40px;

  .adminMainContentWrapper {
    @media (max-width: $lg-breakpoint) {
      max-width: unset;
      overflow: hidden;
    }

    flex: 1 0 100%;
    max-width: calc(100% - 200px);
  }
}

.sidebarRightSpacing {
  @media (max-width: $lg-breakpoint) {
    display: none;
  }

  z-index: 2;
  width: $sidebar-right-spacing;
  background: $white;
}

.mileCountdownContainerMobile {
  @media (max-width: $sm-breakpoint + 80px) {
    display: block;
    width: 100%;
    height: calc(100vw / 4);
    margin-top: -30px;
    margin-bottom: 30px;
  }

  display: none;
}

.mileCountdownContainer {
  @media (max-width: $sm-breakpoint + 80px) {
    display: none;
  }

  @media (max-width: $lg-breakpoint) {
    width: 380px;
    height: 90px;
    margin-top: -34px;
  }

  width: 416px;
  height: 104px;
  margin-top: -10px;
  margin-left: 20px;
}
</style>
